import { Component, Injector, ElementRef, ViewChild, AfterViewInit, NgZone } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ArtifactBaseComponent } from '../artifact-base-component';
import { APPLICATION_STATUS } from 'src/app/core/enums/application.enum';
import { MiscellaneousService } from 'src/app/core/services/miscellaneous.service';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
  selector: 'app-document-review',
  templateUrl: './document-review.component.html',
  styleUrls: ['./document-review.component.scss']
})
export class DocumentReviewComponent extends ArtifactBaseComponent implements AfterViewInit {

  @ViewChild('pdfContainer') pdfContainer!: ElementRef<HTMLDivElement>;

  loading: boolean = false;
  error: string = '';
  totalPages: number = 0;
  hasScrolledToEnd: boolean = false;
  acknowledged: boolean = false;
  fileName: string = '';

  private pdfDoc: any = null;
  private scrollThreshold = 50; // pixels from bottom to consider "scrolled to end"
  private boundOnScroll = this.onScroll.bind(this);
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private injector: Injector,
    private sanitizer: DomSanitizer,
    private miscService: MiscellaneousService,
    private zone: NgZone
  ) {
    super(injector);
  }

  ngAfterViewInit() {
    if (this.pdfContainer) {
      this.pdfContainer.nativeElement.addEventListener('scroll', this.boundOnScroll);
      this.observeContainerSize();
    }
  }

  initJsonData() {
    // If previously completed, restore state without requiring re-scroll or re-check
    if (this.jsonData?.sectionValid) {
      this.hasScrolledToEnd = true;
      this.acknowledged = true;
      this.artifact.sectionValid = true;
      return;
    }

    // Load PDF if not already loaded
    if (!this.pdfDoc && !this.loading) {
      this.loadPdf();
    }
  }

  validateJsonData() {}

  resetJsonData() {
    this.hasScrolledToEnd = false;
    this.acknowledged = false;
    this.artifact.sectionValid = false;
    this.artifact.isArtifactUpdated = true;
    this.pdfDoc = null;
    // Re-evaluate after clearing the flag, otherwise a document that needs no
    // scrolling leaves the acknowledgement unreachable again after a redo.
    setTimeout(() => this.checkScrollable());
    return [];
  }

  override processBeforeSubmit() {
    this.jsonData = {
      sectionValid: this.artifact.sectionValid,
      acknowledgedAt: this.artifact.sectionValid ? new Date().toISOString() : null,
      fileName: this.fileName
    };
  }

  private loadPdf() {
    const approach = this.appWizService.appWizard?.approach;
    const templateType = this.artifact.templateType;

    if (!approach || !templateType) {
      this.error = 'Unable to determine document to display.';
      return;
    }

    this.loading = true;
    this.error = '';

    this.miscService.getTemplateFileContent(approach, templateType).subscribe({
      next: (res: any) => {
        this.fileName = res.fileName || '';
        if (res.fileContent) {
          this.renderPdf(res.fileContent);
        } else {
          this.error = 'No document content available.';
          this.loading = false;
        }
      },
      error: (err: any) => {
        this.error = 'Unable to load document. Please try again later.';
        this.loading = false;
      }
    });
  }

  private async renderPdf(base64Content: string) {
    try {
      const binaryString = atob(base64Content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      this.pdfDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
      this.totalPages = this.pdfDoc.numPages;

      const container = this.pdfContainer.nativeElement;
      container.innerHTML = '';

      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.2 });

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-page';
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        container.appendChild(canvas);

        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport: viewport }).promise;
      }

      this.loading = false;
      // The container is still display:none here - the hidden class only clears on the
      // next change detection pass - so it has no measurable height yet. Defer the
      // check until the container is laid out.
      setTimeout(() => this.checkScrollable());
    } catch (err) {
      this.error = 'Unable to render document.';
      this.loading = false;
    }
  }

  private onScroll() {
    if (this.hasScrolledToEnd || this.isReadOnly()) return;

    const el = this.pdfContainer.nativeElement;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= this.scrollThreshold;

    if (atBottom) {
      this.hasScrolledToEnd = true;
    }
  }

  /**
   * On larger displays the whole document can fit inside the container, so no scroll
   * event ever fires and the acknowledgement stays locked. Treat "nothing to scroll"
   * as already scrolled to the end. Skipped while the container has no height, which
   * is the case before layout and while the artifact panel is collapsed - the resize
   * observer re-runs this once the container is actually visible.
   */
  private checkScrollable() {
    if (this.hasScrolledToEnd || this.isReadOnly() || !this.pdfContainer) return;

    const el = this.pdfContainer.nativeElement;
    if (el.clientHeight > 0 && el.scrollHeight - el.clientHeight <= this.scrollThreshold) {
      this.hasScrolledToEnd = true;
    }
  }

  private observeContainerSize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.checkScrollable());
    });
    this.resizeObserver.observe(this.pdfContainer.nativeElement);
  }

  onAcknowledgeChanged() {
    this.artifact.sectionValid = this.acknowledged;
    this.artifact.isArtifactUpdated = true;
    this.validateSection();
  }

  isDocumentReady(): boolean {
    return !this.loading && !this.error && this.totalPages > 0;
  }

  canAcknowledge(): boolean {
    return this.hasScrolledToEnd && !this.isReadOnly();
  }

  /**
   * The checkbox is always rendered once the document loads, so it has to say why it
   * cannot be used - otherwise a read-only or unscrolled applicant is left guessing.
   */
  getAcknowledgeDisabledReason(): string {
    if (this.appWizService.appWizard?.status?.Value === APPLICATION_STATUS.EXPIRED) {
      return 'This application has expired, so this acknowledgement can no longer be completed.';
    }
    if (this.isReadOnly()) {
      return 'This section is locked and can no longer be changed.';
    }
    return 'Scroll to the end of the document to enable this acknowledgement.';
  }

  getSafeHtml(html: string | undefined): SafeHtml {
    if (html) {
      return this.sanitizer.bypassSecurityTrustHtml(html);
    }
    return '';
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.removeEventListener('scroll', this.boundOnScroll);
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
